import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Settings, 
  Save, 
  ArrowLeft,
  Shield,
  Database,
  Bell,
  Mail,
  Globe,
  Users,
  Key,
  RefreshCw,
  Image,
  Copyright,
  Upload,
  UserPlus,
  UserCog,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Edit3
} from 'lucide-react'
import { useConfig } from '../contexts/ConfigContext'

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    site: {
      name: '无锡市摩恒科技有限公司',
      description: '专业的技术服务提供商',
      keywords: '技术服务, 解决方案, 专业团队',
      language: 'zh-CN',
      timezone: 'Asia/Shanghai'
    },
    identity: {
      logo_pc: '/uploads/logo/logo-pc.png',
      logo_mobile: '/uploads/logo/logo-mobile.png',
      footer_logo_pc: '/uploads/logo/footer-logo-pc.png',
      footer_logo_mobile: '/uploads/logo/footer-logo-mobile.png',
      favicon: '/uploads/logo/favicon.ico',
      site_name: '无锡市摩恒科技有限公司',
      site_domain: 'www.wxmhkj.cn',
      ssl_certificate: 'wxmhkj.cn',
      ssl_expiry_date: '2025-12-31'
    },
    copyright: {
      copyright_text: '© 2024 无锡市摩恒科技有限公司 版权所有',
      icp_number: '苏ICP备XXXXXX号',
      police_record_number: '苏公网安备 XXXXXXXX号',
      icp_link: 'https://beian.miit.gov.cn',
      police_record_link: 'http://www.beian.gov.cn',
      icp_icon: '/uploads/logo/icp-icon.png',
      police_icon: '/uploads/logo/police-icon.png',
      show_icp: true,
      show_police: true
    },
    security: {
      passwordMinLength: 8,
      sessionTimeout: 30,
      maxLoginAttempts: 5,
      enableTwoFactor: false
    },
    email: {
      smtpHost: '',
      smtpPort: 587,
      smtpUser: '',
      smtpPassword: '',
      fromEmail: 'noreply@wxmhkj.cn',
      fromName: '摩恒科技'
    },
    system: {
      maintenanceMode: false,
      logRetentionDays: 90,
      backupFrequency: 'weekly',
      maxUploadSize: 10,
      contact_email: 'info@wxmhkj.cn',
      contact_phone: '0510-88888888',
      company_address: '江苏省无锡市新吴区'
    }
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState('site')
  const [sslCertInfo, setSslCertInfo] = useState(null)
  const [uploadingCert, setUploadingCert] = useState(false)
  const [certUploadResult, setCertUploadResult] = useState(null)
  const navigate = useNavigate()
  const { triggerConfigUpdate } = useConfig()

  useEffect(() => {
    // 检查用户是否已登录
    const token = localStorage.getItem('token')
    const userData = localStorage.getItem('user')
    
    if (!token || !userData) {
      navigate('/admin/login')
      return
    }

    loadSettings()
  }, [navigate])

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/config', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          // 将数据库中的配置转换为前端需要的格式
          const configs = data.data || []
          const settingsData = {}
          
          // 处理网站设置
          settingsData.site = {
            name: configs.find(c => c.config_key === 'site.name')?.config_value || '无锡市摩恒科技有限公司',
            description: configs.find(c => c.config_key === 'site.description')?.config_value || '专业的技术服务提供商',
            keywords: configs.find(c => c.config_key === 'site.keywords')?.config_value || '技术服务, 解决方案, 专业团队',
            language: configs.find(c => c.config_key === 'site.language')?.config_value || 'zh-CN',
            timezone: configs.find(c => c.config_key === 'site.timezone')?.config_value || 'Asia/Shanghai'
          }
          
          // 处理网站标识设置
          settingsData.identity = {
            logo_pc: configs.find(c => c.config_key === 'identity.logo_pc')?.config_value || '/uploads/logo/logo-pc.png',
            logo_mobile: configs.find(c => c.config_key === 'identity.logo_mobile')?.config_value || '/uploads/logo/logo-mobile.png',
            footer_logo_pc: configs.find(c => c.config_key === 'identity.footer_logo_pc')?.config_value || '/uploads/logo/footer-logo-pc.png',
            footer_logo_mobile: configs.find(c => c.config_key === 'identity.footer_logo_mobile')?.config_value || '/uploads/logo/footer-logo-mobile.png',
            favicon: configs.find(c => c.config_key === 'identity.favicon')?.config_value || '/uploads/logo/favicon.ico',
            site_name: configs.find(c => c.config_key === 'identity.site_name')?.config_value || '无锡市摩恒科技有限公司',
            site_domain: configs.find(c => c.config_key === 'identity.site_domain')?.config_value || 'www.wxmhkj.cn',
            ssl_certificate: configs.find(c => c.config_key === 'identity.ssl_certificate')?.config_value || 'wxmhkj.cn',
            ssl_expiry_date: configs.find(c => c.config_key === 'identity.ssl_expiry_date')?.config_value || '2025-12-31'
          }
          
          // 处理版权备案设置
          settingsData.copyright = {
            copyright_text: configs.find(c => c.config_key === 'copyright.copyright_text')?.config_value || '© 2024 无锡市摩恒科技有限公司 版权所有',
            icp_number: configs.find(c => c.config_key === 'copyright.icp_number')?.config_value || '苏ICP备XXXXXX号',
            police_record_number: configs.find(c => c.config_key === 'copyright.police_record_number')?.config_value || '苏公网安备 XXXXXXXX号',
            icp_link: configs.find(c => c.config_key === 'copyright.icp_link')?.config_value || 'https://beian.miit.gov.cn',
            police_record_link: configs.find(c => c.config_key === 'copyright.police_record_link')?.config_value || 'http://www.beian.gov.cn',
            icp_icon: configs.find(c => c.config_key === 'copyright.icp_icon')?.config_value || '/uploads/logo/icp-icon.png',
            police_icon: configs.find(c => c.config_key === 'copyright.police_icon')?.config_value || '/uploads/logo/police-icon.png',
            show_icp: Boolean(configs.find(c => c.config_key === 'copyright.show_icp')?.config_value) || true,
            show_police: Boolean(configs.find(c => c.config_key === 'copyright.show_police')?.config_value) || true
          }
          
          // 处理安全设置
          settingsData.security = {
            passwordMinLength: configs.find(c => c.config_key === 'security.passwordMinLength')?.config_value || 8,
            sessionTimeout: configs.find(c => c.config_key === 'security.sessionTimeout')?.config_value || 30,
            maxLoginAttempts: configs.find(c => c.config_key === 'security.maxLoginAttempts')?.config_value || 5,
            enableTwoFactor: Boolean(configs.find(c => c.config_key === 'security.enableTwoFactor')?.config_value) || false
          }
          
          // 处理邮件设置
          settingsData.email = {
            smtpHost: configs.find(c => c.config_key === 'email.smtpHost')?.config_value || '',
            smtpPort: configs.find(c => c.config_key === 'email.smtpPort')?.config_value || 587,
            smtpUser: configs.find(c => c.config_key === 'email.smtpUser')?.config_value || '',
            smtpPassword: configs.find(c => c.config_key === 'email.smtpPassword')?.config_value || '',
            fromEmail: configs.find(c => c.config_key === 'email.fromEmail')?.config_value || 'noreply@wxmhkj.cn',
            fromName: configs.find(c => c.config_key === 'email.fromName')?.config_value || '摩恒科技'
          }
          
          // 处理系统设置
          settingsData.system = {
            maintenanceMode: Boolean(configs.find(c => c.config_key === 'system.maintenanceMode')?.config_value) || false,
            logRetentionDays: configs.find(c => c.config_key === 'system.logRetentionDays')?.config_value || 90,
            backupFrequency: configs.find(c => c.config_key === 'system.backupFrequency')?.config_value || 'weekly',
            maxUploadSize: configs.find(c => c.config_key === 'system.maxUploadSize')?.config_value || 10,
            contact_email: configs.find(c => c.config_key === 'site.contact_email')?.config_value || 'info@wxmhkj.cn',
            contact_phone: configs.find(c => c.config_key === 'site.contact_phone')?.config_value || '0510-88888888',
            company_address: configs.find(c => c.config_key === 'site.company_address')?.config_value || '江苏省无锡市新吴区'
          }
          
          setSettings(settingsData)
        }
      }
    } catch (error) {
      console.error('加载系统设置失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadSslCertInfo = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/ssl-cert-info', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setSslCertInfo(data.data)
          // 如果后端自动同步了日期，同步更新前端 settings 中的到期日期
          if (data.data.autoSynced && data.data.notAfter) {
            const actualDate = new Date(data.data.notAfter).toISOString().split('T')[0]
            setSettings(prev => ({
              ...prev,
              identity: {
                ...prev.identity,
                ssl_expiry_date: actualDate
              }
            }))
          }
        }
      }
    } catch (error) {
      console.error('获取SSL证书信息失败:', error)
    }
  }

  const handleUploadCert = async () => {
    const certInput = document.getElementById('ssl-cert-file')
    const keyInput = document.getElementById('ssl-key-file')

    if (!certInput?.files[0] || !keyInput?.files[0]) {
      alert('请同时选择证书文件(.pem)和私钥文件(.key)')
      return
    }

    if (!confirm('确定要替换SSL证书吗？上传后nginx将自动重载，新证书立即生效。')) {
      return
    }

    setUploadingCert(true)
    setCertUploadResult(null)

    try {
      const token = localStorage.getItem('token')
      const formData = new FormData()
      formData.append('cert', certInput.files[0])
      formData.append('key', keyInput.files[0])

      const response = await fetch('/api/admin/upload/ssl-cert', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      })

      const data = await response.json()
      if (data.success) {
        setCertUploadResult({ type: 'success', message: data.message })
        certInput.value = ''
        keyInput.value = ''
        await loadSslCertInfo()
      } else {
        setCertUploadResult({ type: 'error', message: data.message || '上传失败' })
      }
    } catch (error) {
      setCertUploadResult({ type: 'error', message: '上传失败: ' + error.message })
    } finally {
      setUploadingCert(false)
    }
  }

  useEffect(() => {
    if (!loading) {
      loadSslCertInfo()
    }
  }, [loading])

  const handleSave = async () => {
    setSaving(true)
    try {
      const token = localStorage.getItem('token')
      
      // 将设置转换为后端需要的扁平化格式
      const configs = {}
      
      // 网站设置 - 添加空对象检查
      if (settings.site && typeof settings.site === 'object' && Object.keys(settings.site).length > 0) {
        Object.entries(settings.site).forEach(([key, value]) => {
          configs[`site.${key}`] = value
        })
      }
      
      // 网站标识设置 - 添加空对象检查
      if (settings.identity && typeof settings.identity === 'object' && Object.keys(settings.identity).length > 0) {
        Object.entries(settings.identity).forEach(([key, value]) => {
          configs[`identity.${key}`] = value
        })
      }
      
      // 版权备案设置 - 添加空对象检查
      if (settings.copyright && typeof settings.copyright === 'object' && Object.keys(settings.copyright).length > 0) {
        Object.entries(settings.copyright).forEach(([key, value]) => {
          configs[`copyright.${key}`] = value
        })
      }
      
      // 安全设置 - 添加空对象检查
      if (settings.security && typeof settings.security === 'object' && Object.keys(settings.security).length > 0) {
        Object.entries(settings.security).forEach(([key, value]) => {
          configs[`security.${key}`] = value
        })
      }
      
      // 邮件设置 - 添加空对象检查
      if (settings.email && typeof settings.email === 'object' && Object.keys(settings.email).length > 0) {
        Object.entries(settings.email).forEach(([key, value]) => {
          configs[`email.${key}`] = value
        })
      }
      
      // 系统设置（不包括联系我们信息）- 添加空对象检查
      if (settings.system && typeof settings.system === 'object' && Object.keys(settings.system).length > 0) {
        Object.entries(settings.system).forEach(([key, value]) => {
          // 联系我们信息使用 site. 前缀，其他系统设置使用 system. 前缀
          if (key === 'contact_email' || key === 'contact_phone' || key === 'company_address') {
            configs[`site.${key}`] = value
          } else {
            configs[`system.${key}`] = value
          }
        })
      }

      const response = await fetch('/api/admin/config', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configs)
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success) {
          alert('系统设置已保存')
          // 保存成功后重新加载设置数据
          try {
            await loadSettings()
            // 触发配置更新事件，通知所有组件重新加载配置
            triggerConfigUpdate()
          } catch (loadError) {
            console.error('重新加载设置失败:', loadError)
            // 不显示错误提示，因为保存已经成功
            // 用户可以手动刷新页面查看更新后的设置
          }
        } else {
          alert('保存失败：' + result.message)
        }
      } else {
        alert('保存失败，请检查网络连接')
      }
    } catch (error) {
      console.error('保存系统设置失败:', error)
      alert('保存失败，请重试')
    } finally {
      setSaving(false)
    }
  }

  const handleInputChange = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }))
  }

  // ==================== 账号管理 ====================
  const [accounts, setAccounts] = useState([])
  const [accountsLoading, setAccountsLoading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [newAccount, setNewAccount] = useState({
    username: '', email: '', password: '', display_name: '', role: 'content_admin'
  })
  const [resetPassword, setResetPassword] = useState({ newPassword: '' })

  const loadAccounts = async () => {
    setAccountsLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/accounts', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await response.json()
      if (data.success) {
        setAccounts(data.data || [])
      }
    } catch (e) {
      console.error('加载账号列表失败:', e)
    } finally {
      setAccountsLoading(false)
    }
  }

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) loadAccounts()
  }, [])

  const handleCreateAccount = async () => {
    if (!newAccount.username || !newAccount.email || !newAccount.password) {
      alert('用户名、邮箱和密码不能为空')
      return
    }
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/accounts', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAccount)
      })
      const data = await response.json()
      if (data.success) {
        alert('账号创建成功')
        setShowCreateModal(false)
        setNewAccount({ username: '', email: '', password: '', display_name: '', role: 'content_admin' })
        loadAccounts()
      } else {
        alert('创建失败：' + data.message)
      }
    } catch (e) {
      console.error('创建账号失败:', e)
      alert('创建失败，请重试')
    }
  }

  const handleResetPassword = async () => {
    if (!resetPassword.newPassword || resetPassword.newPassword.length < 6) {
      alert('新密码至少需要6个字符')
      return
    }
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/admin/accounts/${selectedAccount.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newPassword: resetPassword.newPassword })
      })
      const data = await response.json()
      if (data.success) {
        alert('密码重置成功')
        setShowPasswordModal(false)
        setResetPassword({ newPassword: '' })
      } else {
        alert('重置失败：' + data.message)
      }
    } catch (e) {
      console.error('重置密码失败:', e)
      alert('重置失败，请重试')
    }
  }

  const handleToggleAccount = async (account) => {
    if (!confirm(`确定要${account.is_active ? '禁用' : '启用'}账号 "${account.username}" 吗？`)) return
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/admin/accounts/${account.id}/toggle-status`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await response.json()
      if (data.success) {
        alert(data.message)
        loadAccounts()
      } else {
        alert(data.message)
      }
    } catch (e) {
      console.error('切换状态失败:', e)
    }
  }

  const handleDeleteAccount = async (account) => {
    if (!confirm(`确定要删除账号 "${account.username}" 吗？此操作不可撤销！`)) return
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/admin/accounts/${account.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await response.json()
      if (data.success) {
        alert('账号已删除')
        loadAccounts()
      } else {
        alert(data.message)
      }
    } catch (e) {
      console.error('删除账号失败:', e)
    }
  }

  const roleLabel = (role) => {
    const map = { super_admin: '超级管理员', content_admin: '内容管理员', operation_admin: '运维管理员', security_admin: '安全管理员' }
    return map[role] || role
  }

  const handleTestEmail = async () => {
    try {
      // 获取当前用户的邮箱或使用默认测试邮箱
      const userData = localStorage.getItem('user')
      const user = userData ? JSON.parse(userData) : null
      const testEmail = user?.email || 'test@example.com'
      
      // 询问用户使用哪个邮箱进行测试
      const email = prompt('请输入测试邮箱地址:', testEmail)
      if (!email) return
      
      // 验证邮箱格式
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        alert('请输入有效的邮箱地址')
        return
      }
      
      const token = localStorage.getItem('token')
      
      const response = await fetch('/api/admin/test-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      })
      
      const result = await response.json()
      
      if (result.success) {
        alert(`邮件测试成功！\n\n发送到: ${result.data.to}\n发件人: ${result.data.from}\n主题: ${result.data.subject}\n时间: ${new Date(result.data.timestamp).toLocaleString()}`)
      } else {
        alert(`邮件测试失败: ${result.message}`)
      }
    } catch (error) {
      console.error('测试邮件配置错误:', error)
      alert('邮件测试失败，请检查网络连接和邮件配置')
    }
  }

  const handleBackupNow = async () => {
    try {
      // 询问备份类型
      const backupType = prompt('请选择备份类型:\n1. 数据库备份\n2. 文件备份\n\n输入 "database" 或 "files" (默认: database):', 'database')
      
      if (!backupType) return
      
      if (backupType !== 'database' && backupType !== 'files') {
        alert('无效的备份类型，请输入 "database" 或 "files"')
        return
      }
      
      const token = localStorage.getItem('token')
      
      const response = await fetch('/api/admin/backup', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type: backupType })
      })
      
      const result = await response.json()
      
      if (result.success) {
        if (backupType === 'database') {
          alert(`数据库备份成功！\n\n文件名: ${result.data.fileName}\n文件大小: ${(result.data.size / 1024 / 1024).toFixed(2)} MB\n备份时间: ${new Date(result.data.timestamp).toLocaleString()}`)
        } else {
          alert(`文件备份成功！\n\n备份目录: ${result.data.dirName}\n备份时间: ${new Date(result.data.timestamp).toLocaleString()}`)
        }
      } else {
        alert(`备份失败: ${result.message}`)
      }
    } catch (error) {
      console.error('执行备份错误:', error)
      alert('备份失败，请检查系统配置和权限')
    }
  }

  const handleUploadLogo = async (type) => {
    try {
      // 创建文件输入元素
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      
      input.onchange = async (e) => {
        const file = e.target.files[0]
        if (!file) return
        
        // 验证文件类型
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp', 'image/tiff']
        if (!allowedTypes.includes(file.type)) {
          alert('请选择图片文件 (JPEG, PNG, GIF, WebP, SVG, BMP, TIFF)')
          return
        }
        
        // 验证文件大小 (10MB限制)
        if (file.size > 10 * 1024 * 1024) {
          alert('文件大小不能超过10MB')
          return
        }
        
        // 确定文件类型和配置键
        let fileType, configKey
        if (type === 'PC端') {
          fileType = 'logo_pc'
          configKey = 'identity.logo_pc'
        } else if (type === '移动端') {
          fileType = 'logo_mobile'
          configKey = 'identity.logo_mobile'
        } else if (type === '底部PC端') {
          fileType = 'footer_logo_pc'
          configKey = 'identity.footer_logo_pc'
        } else if (type === '底部移动端') {
          fileType = 'footer_logo_mobile'
          configKey = 'identity.footer_logo_mobile'
        } else if (type === '网站图标') {
          fileType = 'favicon'
          configKey = 'identity.favicon'
        } else {
          alert('不支持的上传类型')
          return
        }
        
        // 创建FormData
        const formData = new FormData()
        formData.append('file', file)
        formData.append('fileType', fileType)
        formData.append('configKey', configKey)
        
        const token = localStorage.getItem('token')
        
        try {
          const response = await fetch('/api/admin/upload/settings', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          })
          
          const result = await response.json()
          
          if (result.success) {
            // 更新对应的设置项
            const category = 'identity'
            const field = fileType === 'logo_pc' ? 'logo_pc' : 
                         fileType === 'logo_mobile' ? 'logo_mobile' : 
                         fileType === 'footer_logo_pc' ? 'footer_logo_pc' :
                         fileType === 'footer_logo_mobile' ? 'footer_logo_mobile' : 'favicon'
            
            handleInputChange(category, field, result.data.fileUrl)
            alert(`${type} LOGO上传成功`)
          } else {
            alert(`上传失败: ${result.message}`)
          }
        } catch (error) {
          console.error('上传文件错误:', error)
          alert('上传失败，请检查网络连接')
        }
      }
      
      input.click()
    } catch (error) {
      console.error('处理文件上传错误:', error)
      alert('上传失败，请重试')
    }
  }

  const handleUploadIcon = async (type) => {
    try {
      // 创建文件输入元素
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      
      input.onchange = async (e) => {
        const file = e.target.files[0]
        if (!file) return
        
        // 验证文件类型
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp', 'image/tiff', 'image/x-icon']
        if (!allowedTypes.includes(file.type)) {
          alert('请选择图片文件 (JPEG, PNG, GIF, WebP, SVG, BMP, TIFF, ICO)')
          return
        }
        
        // 验证文件大小 (5MB限制)
        if (file.size > 5 * 1024 * 1024) {
          alert('文件大小不能超过5MB')
          return
        }
        
        // 确定文件类型和配置键
        let fileType, configKey
        if (type === 'ICP备案') {
          fileType = 'icp_icon'
          configKey = 'copyright.icp_icon'
        } else if (type === '公安备案') {
          fileType = 'police_icon'
          configKey = 'copyright.police_icon'
        } else {
          alert('不支持的上传类型')
          return
        }
        
        // 创建FormData
        const formData = new FormData()
        formData.append('file', file)
        formData.append('fileType', fileType)
        formData.append('configKey', configKey)
        
        const token = localStorage.getItem('token')
        
        try {
          const response = await fetch('/api/admin/upload/settings', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          })
          
          const result = await response.json()
          
          if (result.success) {
            // 更新对应的设置项
            const category = 'copyright'
            const field = fileType === 'icp_icon' ? 'icp_icon' : 'police_icon'
            
            handleInputChange(category, field, result.data.fileUrl)
            alert(`${type}图标上传成功`)
          } else {
            alert(`上传失败: ${result.message}`)
          }
        } catch (error) {
          console.error('上传文件错误:', error)
          alert('上传失败，请检查网络连接')
        }
      }
      
      input.click()
    } catch (error) {
      console.error('处理文件上传错误:', error)
      alert('上传失败，请重试')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-gray-500 hover:text-gray-700 flex items-center space-x-1"
              >
                <ArrowLeft size={16} />
                <span>返回仪表板</span>
              </button>
              <h1 className="text-xl font-bold text-gray-900">
                系统设置
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2"
              >
                {saving ? (
                  <RefreshCw className="animate-spin" size={16} />
                ) : (
                  <Save size={16} />
                )}
                <span>{saving ? '保存中...' : '保存设置'}</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* 主要内容区域 */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* 标签页导航 */}
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                <button
                  onClick={() => setActiveTab('site')}
                  className={`py-4 px-6 text-sm font-medium border-b-2 ${
                    activeTab === 'site'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Globe className="inline mr-2" size={16} />
                  网站设置
                </button>
                <button
                  onClick={() => setActiveTab('identity')}
                  className={`py-4 px-6 text-sm font-medium border-b-2 ${
                    activeTab === 'identity'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Image className="inline mr-2" size={16} />
                  网站标识
                </button>
                <button
                  onClick={() => setActiveTab('copyright')}
                  className={`py-4 px-6 text-sm font-medium border-b-2 ${
                    activeTab === 'copyright'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Copyright className="inline mr-2" size={16} />
                  版权备案
                </button>
                <button
                  onClick={() => setActiveTab('security')}
                  className={`py-4 px-6 text-sm font-medium border-b-2 ${
                    activeTab === 'security'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Shield className="inline mr-2" size={16} />
                  安全设置
                </button>
                <button
                  onClick={() => setActiveTab('email')}
                  className={`py-4 px-6 text-sm font-medium border-b-2 ${
                    activeTab === 'email'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Mail className="inline mr-2" size={16} />
                  邮件设置
                </button>
                <button
                  onClick={() => setActiveTab('system')}
                  className={`py-4 px-6 text-sm font-medium border-b-2 ${
                    activeTab === 'system'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Database className="inline mr-2" size={16} />
                  系统设置
                </button>
                <button
                  onClick={() => { setActiveTab('accounts'); loadAccounts() }}
                  className={`py-4 px-6 text-sm font-medium border-b-2 ${
                    activeTab === 'accounts'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Users className="inline mr-2" size={16} />
                  账号管理
                </button>
              </nav>
            </div>

            {/* 设置内容 */}
            <div className="p-6">
              {/* 网站设置 */}
              {activeTab === 'site' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">网站基本信息</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        网站名称
                      </label>
                      <input
                        type="text"
                        value={settings.site.name}
                        onChange={(e) => handleInputChange('site', 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        网站语言
                      </label>
                      <select
                        value={settings.site.language}
                        onChange={(e) => handleInputChange('site', 'language', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="zh-CN">简体中文</option>
                        <option value="en-US">English</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        网站描述
                      </label>
                      <textarea
                        value={settings.site.description}
                        onChange={(e) => handleInputChange('site', 'description', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        关键词
                      </label>
                      <input
                        type="text"
                        value={settings.site.keywords}
                        onChange={(e) => handleInputChange('site', 'keywords', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="用逗号分隔的关键词"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* 网站标识设置 */}
              {activeTab === 'identity' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">网站标识管理</h3>
                  
                  <div className="space-y-8">
                    {/* LOGO管理 */}
                    <div className="border border-gray-200 rounded-lg p-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">LOGO管理</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            PC端LOGO
                          </label>
                          <div className="flex items-center space-x-4">
                            <div className="w-24 h-24 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                              {settings.identity.logo_pc ? (
                                <img 
                                  src={settings.identity.logo_pc} 
                                  alt="PC Logo" 
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : (
                                <span className="text-gray-400 text-sm">暂无LOGO</span>
                              )}
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={settings.identity.logo_pc}
                                onChange={(e) => handleInputChange('identity', 'logo_pc', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-2"
                                placeholder="LOGO路径或URL"
                              />
                              <button
                                onClick={() => handleUploadLogo('PC端')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm"
                              >
                                <Upload className="inline mr-1" size={14} />
                                上传LOGO
                              </button>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            移动端LOGO
                          </label>
                          <div className="flex items-center space-x-4">
                            <div className="w-24 h-24 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                              {settings.identity.logo_mobile ? (
                                <img 
                                  src={settings.identity.logo_mobile} 
                                  alt="Mobile Logo" 
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : (
                                <span className="text-gray-400 text-sm">暂无LOGO</span>
                              )}
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={settings.identity.logo_mobile}
                                onChange={(e) => handleInputChange('identity', 'logo_mobile', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-2"
                                placeholder="LOGO路径或URL"
                              />
                              <button
                                onClick={() => handleUploadLogo('移动端')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm"
                              >
                                <Upload className="inline mr-1" size={14} />
                                上传LOGO
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* 底部LOGO管理 */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            底部LOGO (PC端)
                          </label>
                          <div className="flex items-center space-x-4">
                            <div className="w-24 h-24 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                              {settings.identity.footer_logo_pc ? (
                                <img 
                                  src={settings.identity.footer_logo_pc} 
                                  alt="Footer PC Logo" 
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : (
                                <span className="text-gray-400 text-sm">暂无LOGO</span>
                              )}
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={settings.identity.footer_logo_pc}
                                onChange={(e) => handleInputChange('identity', 'footer_logo_pc', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-2"
                                placeholder="底部LOGO路径或URL"
                              />
                              <button
                                onClick={() => handleUploadLogo('底部PC端')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm"
                              >
                                <Upload className="inline mr-1" size={14} />
                                上传LOGO
                              </button>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            底部LOGO (移动端)
                          </label>
                          <div className="flex items-center space-x-4">
                            <div className="w-24 h-24 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                              {settings.identity.footer_logo_mobile ? (
                                <img 
                                  src={settings.identity.footer_logo_mobile} 
                                  alt="Footer Mobile Logo" 
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : (
                                <span className="text-gray-400 text-sm">暂无LOGO</span>
                              )}
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={settings.identity.footer_logo_mobile}
                                onChange={(e) => handleInputChange('identity', 'footer_logo_mobile', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-2"
                                placeholder="底部LOGO路径或URL"
                              />
                              <button
                                onClick={() => handleUploadLogo('底部移动端')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm"
                              >
                                <Upload className="inline mr-1" size={14} />
                                上传LOGO
                              </button>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            网站图标 (Favicon)
                          </label>
                          <div className="flex items-center space-x-4">
                            <div className="w-16 h-16 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                              {settings.identity.favicon ? (
                                <img 
                                  src={settings.identity.favicon} 
                                  alt="Favicon" 
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : (
                                <span className="text-gray-400 text-xs">无图标</span>
                              )}
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={settings.identity.favicon}
                                onChange={(e) => handleInputChange('identity', 'favicon', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-2"
                                placeholder="Favicon路径或URL"
                              />
                              <button
                                onClick={() => handleUploadLogo('网站图标')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm"
                              >
                                <Upload className="inline mr-1" size={14} />
                                上传图标
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 网站信息 */}
                    <div className="border border-gray-200 rounded-lg p-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">网站信息</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            网站名称
                          </label>
                          <input
                            type="text"
                            value={settings.identity.site_name}
                            onChange={(e) => handleInputChange('identity', 'site_name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            网站域名
                          </label>
                          <input
                            type="text"
                            value={settings.identity.site_domain}
                            onChange={(e) => handleInputChange('identity', 'site_domain', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="例如：www.wxmhkj.cn"
                          />
                        </div>
                      </div>
                    </div>

                    {/* SSL证书管理 */}
                    <div className="border border-gray-200 rounded-lg p-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">SSL证书管理</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            SSL证书名称
                          </label>
                          <input
                            type="text"
                            value={settings.identity.ssl_certificate}
                            onChange={(e) => handleInputChange('identity', 'ssl_certificate', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="例如：wxmhkj.cn"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            证书到期日期
                          </label>
                          <input
                            type="date"
                            value={settings.identity.ssl_expiry_date}
                            onChange={(e) => handleInputChange('identity', 'ssl_expiry_date', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      {/* 实际SSL证书状态 */}
                      {sslCertInfo && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <h5 className="text-sm font-medium text-gray-700 mb-3">实际证书状态</h5>
                          
                          {/* 证书文件状态 */}
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-sm text-gray-600">证书文件：</span>
                            {sslCertInfo.certExists ? (
                              <span className="inline-flex items-center gap-1 text-sm text-green-600">
                                <CheckCircle size={14} /> 正常
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-sm text-red-600">
                                <XCircle size={14} /> 未找到
                              </span>
                            )}
                            <span className="text-sm text-gray-500 ml-2">密钥文件：</span>
                            {sslCertInfo.keyExists ? (
                              <span className="inline-flex items-center gap-1 text-sm text-green-600">
                                <CheckCircle size={14} /> 正常
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-sm text-red-600">
                                <XCircle size={14} /> 未找到
                              </span>
                            )}
                          </div>

                          {/* 实际到期日期 */}
                          {sslCertInfo.notAfter && (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">实际到期日期：</span>
                                <span className={`text-sm font-medium ${
                                  sslCertInfo.daysRemaining <= 30 ? 'text-red-600' :
                                  sslCertInfo.daysRemaining <= 60 ? 'text-orange-600' :
                                  'text-green-600'
                                }`}>
                                  {new Date(sslCertInfo.notAfter).toLocaleDateString('zh-CN', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit'
                                  })}
                                  {' '}
                                  <span className="text-xs">
                                    (剩余 {sslCertInfo.daysRemaining} 天
                                    {sslCertInfo.daysRemaining <= 0 ? ' — 已过期！' :
                                     sslCertInfo.daysRemaining <= 30 ? ' — 即将过期！' :
                                     sslCertInfo.daysRemaining <= 60 ? ' — 临近过期' : ''})
                                  </span>
                                </span>
                              </div>

                              {/* 与数据库到期日期对比提示 */}
                              {sslCertInfo.notAfter && settings.identity.ssl_expiry_date && (() => {
                                const actualDate = new Date(sslCertInfo.notAfter).toISOString().split('T')[0]
                                const dbDate = settings.identity.ssl_expiry_date
                                if (actualDate !== dbDate) {
                                  return (
                                    <div className="flex items-center gap-2 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
                                      <AlertTriangle size={14} />
                                      数据库中记录的到期日期（{dbDate}）与实际证书到期日期（{actualDate}）不一致，建议更新
                                    </div>
                                  )
                                }
                                return null
                              })()}

                              {/* 签发信息 */}
                              {sslCertInfo.issuer && (
                                <div className="text-xs text-gray-500 mt-1">
                                  签发者：{sslCertInfo.issuer}
                                </div>
                              )}
                              {sslCertInfo.subject && (
                                <div className="text-xs text-gray-500">
                                  颁发给：{sslCertInfo.subject}
                                </div>
                              )}
                            </div>
                          )}

                          {/* 证书错误 */}
                          {sslCertInfo.error && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                              读取证书失败：{sslCertInfo.error}
                            </div>
                          )}
                        </div>
                      )}

                      {/* 证书上传替换 */}
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <h5 className="text-sm font-medium text-blue-900 mb-3">
                          上传新证书替换
                        </h5>
                        <p className="text-xs text-blue-700 mb-3">
                          当证书到期或需要更换时，在此上传新证书文件。系统将自动备份旧证书、写入新证书并重载Nginx。
                          <br />
                          <strong>紧急情况：</strong>如HTTPS已不可用，请通过 <code className="bg-blue-100 px-1 rounded">http://www.wxmhkj.cn/admin</code> 访问本页面更新证书。
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-blue-800 mb-1">
                              证书文件 (.pem)
                            </label>
                            <input
                              id="ssl-cert-file"
                              type="file"
                              accept=".pem,.crt,.cer"
                              className="w-full text-sm text-gray-700 border border-blue-300 rounded-md 
                                       file:mr-3 file:py-2 file:px-3 file:border-0 file:text-sm file:bg-blue-600 
                                       file:text-white hover:file:bg-blue-700 cursor-pointer"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-blue-800 mb-1">
                              私钥文件 (.key)
                            </label>
                            <input
                              id="ssl-key-file"
                              type="file"
                              accept=".key"
                              className="w-full text-sm text-gray-700 border border-blue-300 rounded-md 
                                       file:mr-3 file:py-2 file:px-3 file:border-0 file:text-sm file:bg-blue-600 
                                       file:text-white hover:file:bg-blue-700 cursor-pointer"
                            />
                          </div>
                        </div>
                        <button
                          onClick={handleUploadCert}
                          disabled={uploadingCert}
                          className={`px-4 py-2 rounded-md text-white text-sm font-medium transition-colors ${
                            uploadingCert
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                        >
                          {uploadingCert ? '正在上传并重载Nginx...' : '上传并替换证书'}
                        </button>

                        {certUploadResult && (
                          <div className={`mt-3 p-3 rounded text-sm ${
                            certUploadResult.type === 'success'
                              ? 'bg-green-50 border border-green-200 text-green-700'
                              : 'bg-red-50 border border-red-200 text-red-600'
                          }`}>
                            {certUploadResult.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 版权备案设置 */}
              {activeTab === 'copyright' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">网站版权备案信息管理</h3>
                  
                  <div className="space-y-8">
                    {/* 版权文本 */}
                    <div className="border border-gray-200 rounded-lg p-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">版权信息</h4>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            版权文本
                          </label>
                          <input
                            type="text"
                            value={settings.copyright.copyright_text}
                            onChange={(e) => handleInputChange('copyright', 'copyright_text', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="例如：© 2024 无锡市摩恒科技有限公司 版权所有"
                          />
                        </div>
                      </div>
                    </div>

                    {/* ICP备案信息 */}
                    <div className="border border-gray-200 rounded-lg p-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">ICP备案信息</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            ICP备案号
                          </label>
                          <input
                            type="text"
                            value={settings.copyright.icp_number}
                            onChange={(e) => handleInputChange('copyright', 'icp_number', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="例如：苏ICP备XXXXXX号"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            ICP备案链接
                          </label>
                          <input
                            type="text"
                            value={settings.copyright.icp_link}
                            onChange={(e) => handleInputChange('copyright', 'icp_link', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="例如：https://beian.miit.gov.cn"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            ICP备案图标
                          </label>
                          <div className="flex items-center space-x-4">
                            <div className="w-16 h-16 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                              {settings.copyright.icp_icon ? (
                                <img 
                                  src={settings.copyright.icp_icon} 
                                  alt="ICP Icon" 
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : (
                                <span className="text-gray-400 text-xs">无图标</span>
                              )}
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={settings.copyright.icp_icon}
                                onChange={(e) => handleInputChange('copyright', 'icp_icon', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-2"
                                placeholder="图标路径或URL"
                              />
                              <button
                                onClick={() => handleUploadIcon('ICP备案')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm"
                              >
                                <Upload className="inline mr-1" size={14} />
                                上传图标
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="showIcp"
                            checked={settings.copyright.show_icp}
                            onChange={(e) => handleInputChange('copyright', 'show_icp', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="showIcp" className="ml-2 block text-sm text-gray-700">
                            显示ICP备案信息
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* 公安备案信息 */}
                    <div className="border border-gray-200 rounded-lg p-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">公安备案信息</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            公安备案号
                          </label>
                          <input
                            type="text"
                            value={settings.copyright.police_record_number}
                            onChange={(e) => handleInputChange('copyright', 'police_record_number', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="例如：苏公网安备 XXXXXXXX号"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            公安备案链接
                          </label>
                          <input
                            type="text"
                            value={settings.copyright.police_record_link}
                            onChange={(e) => handleInputChange('copyright', 'police_record_link', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="例如：http://www.beian.gov.cn"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            公安备案图标
                          </label>
                          <div className="flex items-center space-x-4">
                            <div className="w-16 h-16 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                              {settings.copyright.police_icon ? (
                                <img 
                                  src={settings.copyright.police_icon} 
                                  alt="Police Icon" 
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : (
                                <span className="text-gray-400 text-xs">无图标</span>
                              )}
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={settings.copyright.police_icon}
                                onChange={(e) => handleInputChange('copyright', 'police_icon', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-2"
                                placeholder="图标路径或URL"
                              />
                              <button
                                onClick={() => handleUploadIcon('公安备案')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm"
                              >
                                <Upload className="inline mr-1" size={14} />
                                上传图标
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="showPolice"
                            checked={settings.copyright.show_police}
                            onChange={(e) => handleInputChange('copyright', 'show_police', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="showPolice" className="ml-2 block text-sm text-gray-700">
                            显示公安备案信息
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 安全设置 */}
              {activeTab === 'security' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">安全配置</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        密码最小长度
                      </label>
                      <input
                        type="number"
                        min="6"
                        max="20"
                        value={settings.security.passwordMinLength}
                        onChange={(e) => handleInputChange('security', 'passwordMinLength', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        会话超时时间 (分钟)
                      </label>
                      <input
                        type="number"
                        min="5"
                        max="480"
                        value={settings.security.sessionTimeout}
                        onChange={(e) => handleInputChange('security', 'sessionTimeout', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        最大登录尝试次数
                      </label>
                      <input
                        type="number"
                        min="3"
                        max="10"
                        value={settings.security.maxLoginAttempts}
                        onChange={(e) => handleInputChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="enableTwoFactor"
                        checked={settings.security.enableTwoFactor}
                        onChange={(e) => handleInputChange('security', 'enableTwoFactor', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="enableTwoFactor" className="ml-2 block text-sm text-gray-700">
                        启用双因素认证
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* 邮件设置 */}
              {activeTab === 'email' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">邮件服务器配置</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SMTP 服务器
                      </label>
                      <input
                        type="text"
                        value={settings.email.smtpHost}
                        onChange={(e) => handleInputChange('email', 'smtpHost', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="smtp.example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SMTP 端口
                      </label>
                      <input
                        type="number"
                        value={settings.email.smtpPort}
                        onChange={(e) => handleInputChange('email', 'smtpPort', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        用户名
                      </label>
                      <input
                        type="text"
                        value={settings.email.smtpUser}
                        onChange={(e) => handleInputChange('email', 'smtpUser', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        密码
                      </label>
                      <input
                        type="password"
                        value={settings.email.smtpPassword}
                        onChange={(e) => handleInputChange('email', 'smtpPassword', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        发件人邮箱
                      </label>
                      <input
                        type="email"
                        value={settings.email.fromEmail}
                        onChange={(e) => handleInputChange('email', 'fromEmail', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        发件人名称
                      </label>
                      <input
                        type="text"
                        value={settings.email.fromName}
                        onChange={(e) => handleInputChange('email', 'fromName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="pt-4">
                    <button
                      onClick={handleTestEmail}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                      测试邮件配置
                    </button>
                  </div>
                </div>
              )}

              {/* 系统设置 */}
              {activeTab === 'system' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">系统维护设置</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="maintenanceMode"
                        checked={settings.system.maintenanceMode}
                        onChange={(e) => handleInputChange('system', 'maintenanceMode', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="maintenanceMode" className="ml-2 block text-sm text-gray-700">
                        启用维护模式
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        日志保留天数
                      </label>
                      <input
                        type="number"
                        min="7"
                        max="365"
                        value={settings.system.logRetentionDays}
                        onChange={(e) => handleInputChange('system', 'logRetentionDays', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        备份频率
                      </label>
                      <select
                        value={settings.system.backupFrequency}
                        onChange={(e) => handleInputChange('system', 'backupFrequency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="daily">每日</option>
                        <option value="weekly">每周</option>
                        <option value="monthly">每月</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        最大上传大小 (MB)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={settings.system.maxUploadSize}
                        onChange={(e) => handleInputChange('system', 'maxUploadSize', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="pt-4">
                    <button
                      onClick={handleBackupNow}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                      立即备份
                    </button>
                  </div>

                  {/* 联系我们设置 */}
                  <div className="border-t border-gray-200 pt-6 mt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">联系我们设置</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          联系邮箱
                        </label>
                        <input
                          type="email"
                          value={settings.system.contact_email}
                          onChange={(e) => handleInputChange('system', 'contact_email', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="例如：info@wxmhkj.cn"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          联系电话
                        </label>
                        <input
                          type="text"
                          value={settings.system.contact_phone}
                          onChange={(e) => handleInputChange('system', 'contact_phone', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="例如：0510-88888888"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          公司地址
                        </label>
                        <textarea
                          value={settings.system.company_address}
                          onChange={(e) => handleInputChange('system', 'company_address', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="例如：江苏省无锡市新吴区"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 账号管理 */}
              {activeTab === 'accounts' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">管理员账号管理</h3>
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2"
                    >
                      <UserPlus size={16} />
                      <span>新增账号</span>
                    </button>
                  </div>

                  {accountsLoading ? (
                    <div className="text-center py-12">
                      <RefreshCw className="animate-spin mx-auto text-gray-400" size={32} />
                      <p className="mt-2 text-gray-500">加载中...</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">账号</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">角色</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">最后登录</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">操作</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {accounts.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                暂无管理员账号
                              </td>
                            </tr>
                          ) : (
                            accounts.map(account => (
                              <tr key={account.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                      <span className="text-blue-600 font-medium text-sm">
                                        {account.username?.charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                    <div className="ml-4">
                                      <div className="text-sm font-medium text-gray-900">{account.display_name || account.username}</div>
                                      <div className="text-sm text-gray-500">{account.email}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                    {roleLabel(account.role)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    account.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {account.is_active ? <CheckCircle size={12} className="mr-1" /> : <XCircle size={12} className="mr-1" />}
                                    {account.is_active ? '正常' : '禁用'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {account.lastLoginAt ? new Date(account.lastLoginAt).toLocaleString('zh-CN') : '从未登录'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                  <button
                                    onClick={() => { setSelectedAccount(account); setShowPasswordModal(true); setResetPassword({ newPassword: '' }) }}
                                    className="text-blue-600 hover:text-blue-900"
                                    title="重置密码"
                                  >
                                    <Key size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleToggleAccount(account)}
                                    className={account.is_active ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}
                                    title={account.is_active ? '禁用' : '启用'}
                                  >
                                    {account.is_active ? <XCircle size={16} /> : <CheckCircle size={16} />}
                                  </button>
                                  <button
                                    onClick={() => handleDeleteAccount(account)}
                                    className="text-red-600 hover:text-red-900"
                                    title="删除"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 创建账号对话框 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <UserPlus className="mr-2" size={20} />
              新增管理员账号
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">用户名 *</label>
                <input
                  type="text" value={newAccount.username}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="4-50个字符"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">邮箱 *</label>
                <input
                  type="email" value={newAccount.email}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">密码 *</label>
                <input
                  type="password" value={newAccount.password}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="至少6个字符"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">显示名称</label>
                <input
                  type="text" value={newAccount.display_name}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, display_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">角色</label>
                <select
                  value={newAccount.role}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="super_admin">超级管理员</option>
                  <option value="content_admin">内容管理员</option>
                  <option value="operation_admin">运维管理员</option>
                  <option value="security_admin">安全管理员</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">取消</button>
              <button onClick={handleCreateAccount} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md">创建</button>
            </div>
          </div>
        </div>
      )}

      {/* 重置密码对话框 */}
      {showPasswordModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Key className="mr-2" size={20} />
              重置密码 - {selectedAccount.username}
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
              <input
                type="password" value={resetPassword.newPassword}
                onChange={(e) => setResetPassword({ newPassword: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="至少6个字符"
              />
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button onClick={() => setShowPasswordModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">取消</button>
              <button onClick={handleResetPassword} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md">确认重置</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminSettings
